import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Mail } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { pricingTiers, vehicleTypes } from '../data/pricing';
import {
  binCleaningPrices,
  binCleaningFeatures,
  binCleaningBenefits,
} from '../data/binCleaning';
import { BUSINESS_INFO } from '../utils/constants';
import WhatsAppIcon from '../components/ui/WhatsAppIcon';

const Pricing = () => {
  const [selectedVehicle, setSelectedVehicle] = useState('medium');

  return (
    <div>
      {/* ============ PAGE HERO ============ */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="eyebrow">Pricing</div>
            <h1>Transparent Pricing</h1>
            <p>
              Clear, upfront pricing with no hidden fees. Choose your vehicle size
              to see exact costs.
            </p>
          </div>
        </div>
      </section>

      {/* ============ CAR VALETING PRICING (UNCHANGED) ============ */}
      <section className="section section-dark">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem' }}>Select Your Vehicle Type</h2>
            <p style={{ color: 'var(--gray)' }}>Pricing varies by vehicle size</p>
          </div>

          <div className="pricing-selector">
            {vehicleTypes.map((type) => (
              <div
                key={type.id}
                onClick={() => setSelectedVehicle(type.id)}
                className={`pricing-option ${selectedVehicle === type.id ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedVehicle(type.id)}
              >
                <Car />
                <div className="pricing-option-name">{type.name}</div>
                <div className="pricing-option-examples">{type.examples}</div>
              </div>
            ))}
          </div>

          <div className="pricing-cards">
            {pricingTiers.map((tier) => {
              const isPopular = tier.id === 'silver';
              return (
                <Card
                  key={tier.id}
                  hover
                  className={`pricing-card ${isPopular ? 'popular' : ''}`}
                >
                  {isPopular && (
                    <div className="pricing-card-badge">Most Popular</div>
                  )}

                  <div className="pricing-card-header">
                    <h3 className="pricing-card-name">{tier.name}</h3>
                    <div className="pricing-card-price">
                      £{tier.vehicles[selectedVehicle]}
                    </div>
                    <div className="pricing-card-vehicle">
                      for {vehicleTypes.find((v) => v.id === selectedVehicle)?.name}
                    </div>
                  </div>

                  <a
                    href={BUSINESS_INFO.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginTop: 'auto' }}
                  >
                    <Button
                      variant={isPopular ? 'primary' : 'outline'}
                      className="w-full"
                      icon={WhatsAppIcon}
                    >
                      Book {tier.name}
                    </Button>
                  </a>
                </Card>
              );
            })}
          </div>

          <Card style={{ padding: '2rem', marginTop: '2rem' }}>
            <h3
              style={{
                textAlign: 'center',
                marginBottom: '1.5rem',
                color: 'var(--white)',
              }}
            >
              What's Included in Every Service
            </h3>
            <div className="checklist">
              <div className="checklist-item">
                <Car size={20} />
                <span>Mobile service — we come to you</span>
              </div>
              <div className="checklist-item">
                <Car size={20} />
                <span>Professional-grade products</span>
              </div>
              <div className="checklist-item">
                <Car size={20} />
                <span>Trained and experienced technicians</span>
              </div>
            </div>
          </Card>

          <div className="text-center" style={{ marginTop: '3rem' }}>
            <h3 style={{ color: 'var(--white)', marginBottom: '1rem' }}>
              Need a Custom Quote?
            </h3>
            <p style={{ marginBottom: '1.5rem', color: 'var(--gray)' }}>
              For fleet services, special vehicles, or custom packages, contact us
              for a tailored quote.
            </p>
            <div
              className="flex gap-md justify-center"
              style={{ flexWrap: 'wrap' }}
            >
              <a
                href={BUSINESS_INFO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="whatsapp" size="lg" icon={WhatsAppIcon}>
                  Get Quote on WhatsApp
                </Button>
              </a>
              <a href={`mailto:${BUSINESS_INFO.email}`}>
                <Button variant="outline" size="lg" icon={Mail}>
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BIN CLEANING PRICING (NEW) ============ */}
      <section className="section section-black" id="bin-cleaning">
        <div className="container">
          <div className="section-header">
            <div className="eyebrow">New Service</div>
            <h2>Bin Cleaning Prices</h2>
            <p>
              Professional wheelie bin cleaning for domestic and commercial bins
              — deep cleaned, sanitised and deodorised at the kerbside.
            </p>
          </div>

          {/* --- Domestic Pricing --- */}
          <h3
            style={{
              color: 'var(--gold)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
              marginBottom: '1.25rem',
              textAlign: 'center',
            }}
          >
            Domestic Bin Cleaning
          </h3>

          <div className="pricing-cards bin-cleaning-domestic-row" style={{ marginBottom: '3rem' }}>
            {binCleaningPrices
              .filter((p) => p.category === 'domestic')
              .map((price) => (
                <Card
                  key={price.id}
                  hover
                  className={`pricing-card ${price.popular ? 'popular' : ''}`}
                >
                  {price.badge && (
                    <div className="pricing-card-badge">{price.badge}</div>
                  )}
                  <div className="pricing-card-header">
                    <h3 className="pricing-card-name">{price.name}</h3>
                    <div className="pricing-card-price">{price.price}</div>
                    <div className="pricing-card-vehicle">{price.unit}</div>
                    <div
                      style={{
                        marginTop: '0.75rem',
                        fontSize: '0.8125rem',
                        color: 'var(--gray)',
                      }}
                    >
                      {price.sizes}
                    </div>
                    <p
                      style={{
                        marginTop: '0.75rem',
                        color: 'var(--text-secondary)',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {price.description}
                    </p>
                  </div>
                  <a
                    href={BUSINESS_INFO.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginTop: 'auto' }}
                  >
                    <Button
                      variant={price.popular ? 'primary' : 'outline'}
                      className="w-full"
                      icon={WhatsAppIcon}
                    >
                      Book Now
                    </Button>
                  </a>
                </Card>
              ))}
          </div>

          {/* --- Commercial Pricing --- */}
          <h3
            style={{
              color: 'var(--gold)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
              marginBottom: '1.25rem',
              textAlign: 'center',
            }}
          >
            Commercial Bin Cleaning Starts
          </h3>

          <div style={{ maxWidth: '420px', margin: '0 auto 3rem' }}>
            {binCleaningPrices
              .filter((p) => p.category === 'commercial')
              .map((price) => (
                <Card key={price.id} hover className="pricing-card popular">
                  {price.badge && (
                    <div className="pricing-card-badge">{price.badge}</div>
                  )}
                  <div className="pricing-card-header">
                    <h3 className="pricing-card-name">{price.name}</h3>
                    <div className="pricing-card-price">{price.price}</div>
                    <div className="pricing-card-vehicle">{price.unit}</div>
                    <div
                      style={{
                        marginTop: '0.75rem',
                        fontSize: '0.8125rem',
                        color: 'var(--gray)',
                      }}
                    >
                      {price.sizes}
                    </div>
                    <p
                      style={{
                        marginTop: '0.75rem',
                        color: 'var(--text-secondary)',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {price.description}
                    </p>
                  </div>
                  <a
                    href={BUSINESS_INFO.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="primary"
                      className="w-full"
                      icon={WhatsAppIcon}
                    >
                      Book Commercial
                    </Button>
                  </a>
                </Card>
              ))}
          </div>

          {/* --- What's Included --- */}
          <div className="section-header" style={{ marginTop: '4rem' }}>
            <div className="eyebrow">What's Included</div>
            <h2>What's Included in the Service</h2>
            <p>ARA Professional Bin Cleaning service includes:</p>
          </div>

          <div className="grid grid-3" style={{ marginBottom: '4rem' }}>
            {binCleaningFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} hover className="value-card">
                  <div className="value-icon">
                    <Icon size={28} />
                  </div>
                  <h3
                    style={{
                      fontSize: '1.0625rem',
                      marginBottom: '0.5rem',
                      color: 'var(--white)',
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p className="value-description">{feature.description}</p>
                </Card>
              );
            })}
          </div>

          {/* --- Why Choose Regular Bin Cleaning --- */}
          <div className="section-header">
            <div className="eyebrow">Why Subscribe</div>
            <h2>Why Choose Regular Bin Cleaning?</h2>
            <p>
              Choosing a subscription plan helps keep your bins hygienic
              year-round, reduces unpleasant odours and helps prevent pest
              infestations. One-off cleans are ideal for occasional maintenance
              or for first-time customers.
            </p>
          </div>

          <div className="grid grid-4" style={{ marginBottom: '4rem' }}>
            {binCleaningBenefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} hover className="value-card">
                  <div className="value-icon">
                    <Icon size={28} />
                  </div>
                  <h3
                    style={{
                      fontSize: '1.0625rem',
                      marginBottom: '0.5rem',
                      color: 'var(--white)',
                    }}
                  >
                    {benefit.title}
                  </h3>
                  <p className="value-description">{benefit.description}</p>
                </Card>
              );
            })}
          </div>

          {/* --- Final CTA --- */}
          <div className="cta-section">
            <h2>Keep Your Bins Clean &amp; Fresh</h2>
            <p>
              Book your professional bin cleaning service today and enjoy
              cleaner, fresher and more hygienic bins.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <a
                href={BUSINESS_INFO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="primary" size="lg" icon={WhatsAppIcon}>
                  Book a Bin Clean
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="secondary" size="lg">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;